import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  InviteLocaleSelect,
  useDefaultInviteLocale,
} from '@/components/InviteLocaleSelect';
import { useHubOutletContext } from './TournamentHubLayoutRoute';
import { currentTenantOrNull } from '@/lib/tenants';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import {
  listCoDirectors,
  getCoDirectorPool,
  inviteCoDirector,
  revokeCoDirectorInvite,
  resendCoDirectorInvite,
  addCoDirectorFromPool,
  removeCoDirector,
} from '@/lib/api/co-directors';
import type {
  CoDirectorListResponse,
  CoDirectorPoolEntry,
} from '@swiss-magic/shared-types';

// Story 094 — owner-only co-director management. Public offering: invite
// by email + manage pending invites. White-label: add from the director
// pool. The owner gate is enforced server-side; we also hide the page
// chrome for co-directors who reach the URL directly.

export function CoDirectorsPage() {
  const { t } = useTranslation('director');
  const { tournament, role } = useHubOutletContext();
  const tournamentId = tournament?.id ?? '';
  const isWhiteLabel = currentTenantOrNull()?.isWhiteLabel ?? false;

  const [data, setData] = useState<CoDirectorListResponse | null>(null);
  const [pool, setPool] = useState<CoDirectorPoolEntry[]>([]);
  const [email, setEmail] = useState('');
  const defaultLocale = useDefaultInviteLocale();
  const [locale, setLocale] = useState(defaultLocale);
  const [selectedPoolUser, setSelectedPoolUser] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const list = await listCoDirectors(tournamentId);
      setData(list);
      if (isWhiteLabel) {
        setPool(await getCoDirectorPool(tournamentId));
      }
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('co_directors.load_failed'),
      );
    }
  }, [tournamentId, isWhiteLabel, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Gate every action on the tournament being loaded — otherwise
  // tournamentId is '' and mutations hit /tournaments//co-directors/...
  // (404). The hub fetch can lag on a slow backend.
  if (!tournament) {
    return (
      <div data-testid="co-directors-page" className="mx-auto max-w-2xl">
        <p className="text-muted-foreground">{t('tournament_hub.loading')}</p>
      </div>
    );
  }

  if (role !== 'owner') {
    return (
      <div data-testid="co-directors-page" className="mx-auto max-w-2xl">
        <p data-testid="co-directors-forbidden" className="text-muted-foreground">
          {t('co_directors.owner_only')}
        </p>
      </div>
    );
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await inviteCoDirector(tournamentId, email.trim(), locale);
      showSuccessToast(t('co_directors.invite_sent'));
      setEmail('');
      await reload();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('co_directors.invite_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(inviteId: string) {
    setBusy(true);
    try {
      await revokeCoDirectorInvite(tournamentId, inviteId);
      showSuccessToast(t('co_directors.invite_revoked'));
      await reload();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('co_directors.action_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleResend(inviteId: string) {
    setBusy(true);
    try {
      await resendCoDirectorInvite(tournamentId, inviteId);
      showSuccessToast(t('co_directors.invite_resent'));
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('co_directors.action_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleAddFromPool() {
    if (!selectedPoolUser) return;
    setBusy(true);
    try {
      await addCoDirectorFromPool(tournamentId, selectedPoolUser);
      showSuccessToast(t('co_directors.added'));
      setSelectedPoolUser('');
      await reload();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('co_directors.action_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(userId: string) {
    setBusy(true);
    try {
      await removeCoDirector(tournamentId, userId);
      showSuccessToast(t('co_directors.removed'));
      await reload();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('co_directors.action_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="co-directors-page" className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-xl font-semibold">{t('co_directors.title')}</h1>

      {/* Add flow — differs by deployment. */}
      {isWhiteLabel ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('co_directors.add_from_pool_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3" data-testid="co-director-pool-picker">
            <select
              data-testid="co-director-pool-select"
              value={selectedPoolUser}
              onChange={(e) => setSelectedPoolUser(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
            >
              <option value="">{t('co_directors.pool_placeholder')}</option>
              {pool.map((d) => (
                <option key={d.userId} value={d.userId}>
                  {d.name} ({d.email})
                </option>
              ))}
            </select>
            <Button
              type="button"
              data-testid="co-director-pool-add"
              disabled={busy || !selectedPoolUser}
              onClick={handleAddFromPool}
            >
              {t('co_directors.add_button')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('co_directors.invite_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleInvite}
              className="flex flex-col gap-3"
              data-testid="co-director-invite-form"
            >
              <Input
                type="email"
                required
                placeholder={t('co_directors.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="co-director-email-input"
              />
              <InviteLocaleSelect
                value={locale}
                onChange={setLocale}
                disabled={busy}
                labelText={t('co_directors.locale_label')}
                hintText={t('co_directors.locale_hint')}
              />
              <Button
                type="submit"
                data-testid="co-director-invite-submit"
                disabled={busy}
              >
                {t('co_directors.invite_button')}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Pending invites (public only). */}
      {!isWhiteLabel && (data?.pendingInvites.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('co_directors.pending_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {data?.pendingInvites.map((inv) => (
              <div
                key={inv.id}
                data-testid={`co-director-pending-${inv.email}`}
                className="flex items-center justify-between gap-2 rounded border px-3 py-2 text-sm"
              >
                <span className="font-mono">{inv.email}</span>
                <span className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid={`co-director-resend-${inv.email}`}
                    disabled={busy}
                    onClick={() => handleResend(inv.id)}
                  >
                    {t('co_directors.resend_button')}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    data-testid={`co-director-revoke-${inv.email}`}
                    disabled={busy}
                    onClick={() => handleRevoke(inv.id)}
                  >
                    {t('co_directors.revoke_button')}
                  </Button>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Active co-directors. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('co_directors.active_title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2" data-testid="co-directors-list">
          {(data?.coDirectors.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('co_directors.none_yet')}
            </p>
          ) : (
            data?.coDirectors.map((cd) => (
              <div
                key={cd.userId}
                data-testid={`co-director-row-${cd.email}`}
                className="flex items-center justify-between gap-2 rounded border px-3 py-2 text-sm"
              >
                <span>
                  <span className="font-medium">{cd.name}</span>{' '}
                  <span className="font-mono text-muted-foreground">
                    {cd.email}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  data-testid={`co-director-remove-${cd.email}`}
                  disabled={busy}
                  onClick={() => handleRemove(cd.userId)}
                >
                  {t('co_directors.remove_button')}
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
